import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthLayout } from "@/pages/Login";
import { CodeInput } from "@/components/security";
import { ClassificationPicker } from "@/components/registration";

export default function Register() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [schemas, setSchemas] = useState([]);
  const [cls, setCls] = useState(sessionStorage.getItem("reg_classification") || "");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", password: "", confirm: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => { api.get("/registration/schemas").then((r) => setSchemas(r.data)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const createAccount = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.password.length < 8) errs.password = "Use at least 8 characters";
    if (form.password !== form.confirm) errs.confirm = "Passwords do not match";
    if (form.mobile && !/^(09|\+639)\d{9}$/.test(form.mobile)) errs.mobile = "Use 09XXXXXXXXX or +639XXXXXXXXX";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { confirm, ...body } = form;
      await api.post("/auth/register", { ...body, mobile: body.mobile || null }, { skipRefresh: true });
      toast.success("Check your email for a verification code");
      setStep(3);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/register/verify-email", { email: form.email, code }, { skipRefresh: true });
      toast.success("Email verified. Sign in to complete your application.");
      setStep(4);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const resend = async () => {
    try { await api.post("/auth/register/resend", { email: form.email }, { skipRefresh: true }); toast.success("A new code was sent"); }
    catch (ex) { toast.error(errMsg(ex)); }
  };
  const field = (k, label, type = "text", req = true) => (
    <div className="space-y-1.5">
      <Label>{label}{req ? <span className="text-rose-600"> *</span> : <span className="text-slate-400 text-xs"> (optional)</span>}</Label>
      <Input type={type} value={form[k]} onChange={set(k)} required={req} data-testid={`register-${k}-input`} />
      {errors[k] && <p className="text-xs text-rose-600">{errors[k]}</p>}
    </div>
  );

  return (
    <AuthLayout title="Subscriber registration" subtitle={`Step ${Math.min(step, 3)} of 3 · ${["Account type", "Login details", "Verify email"][Math.min(step, 3) - 1]}`}>
      {step === 1 && (
        <div className="space-y-5">
          <ClassificationPicker schemas={schemas} value={cls} onChange={setCls} />
          <Button className="w-full" disabled={!cls} onClick={() => { sessionStorage.setItem("reg_classification", cls); setStep(2); }} data-testid="register-classification-next">Continue</Button>
        </div>
      )}
      {step === 2 && (
        <form onSubmit={createAccount} className="space-y-4" data-testid="register-account-form">
          <p className="text-xs text-slate-500">These are your personal sign-in details{cls !== "residential" ? " as the authorized representative" : ""}. You will complete the {cls} application after verifying your email.</p>
          <div className="grid grid-cols-2 gap-3">{field("first_name", "First name")}{field("last_name", "Last name")}</div>
          {field("email", "Email", "email")}
          {field("mobile", "Mobile number", "tel", false)}
          {field("password", "Password", "password")}
          {field("confirm", "Confirm password", "password")}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button type="submit" className="flex-1" disabled={busy} data-testid="register-submit-button">Create login & send code</Button>
          </div>
        </form>
      )}
      {step === 3 && (
        <div className="space-y-4" data-testid="register-verify-panel">
          <p className="text-sm text-slate-600">Enter the 6-digit code sent to <b>{form.email}</b>. This verifies your email address only; Two-Factor Authentication can be set up separately.</p>
          <CodeInput value={code} onChange={setCode} testId="register-verify-code-input" />
          <div className="flex gap-2">
            <Button className="flex-1" onClick={verify} disabled={busy || code.length !== 6} data-testid="register-verify-button">Verify email</Button>
            <Button variant="outline" onClick={resend} data-testid="register-resend-button">Resend</Button>
          </div>
        </div>
      )}
      {step === 4 && (
        <div className="space-y-4" data-testid="register-done-panel">
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">Email verified. Sign in to continue your {cls} registration application.</p>
          <Button className="w-full" onClick={() => nav("/login")} data-testid="register-go-login">Go to sign in</Button>
        </div>
      )}
      <p className="text-sm text-slate-500 mt-6">Already registered? <Link to="/login" className="text-amber-700 hover:underline">Sign in</Link></p>
    </AuthLayout>
  );
}
